import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Search, Check, X, Link2, Unplug, FlaskConical, Inbox, Clock, MapPin, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import ConnectionChat from "@/components/ConnectionChat";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FindLab({ token, onChanged }) {
    const [labs, setLabs] = useState([]);
    const [conns, setConns] = useState({ connected: [], incoming: [], outgoing: [] });
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(null);
    const [chatOpen, setChatOpen] = useState(null);

    const load = useCallback(async (query = "") => {
        const headers = { "X-Practice-Token": token };
        try {
            const [l, c] = await Promise.all([
                axios.get(`${API}/connect/labs`, { headers, params: { q: query } }),
                axios.get(`${API}/connect/practice/connections`, { headers }),
            ]);
            setLabs(l.data);
            setConns(c.data);
        } catch { toast.error("Could not load labs"); }
        setLoading(false);
    }, [token]);

    useEffect(() => {
        const t = setTimeout(() => load(q), q ? 350 : 0);
        return () => clearTimeout(t);
    }, [q, load]);

    const act = async (fn, id) => {
        setBusy(id);
        try {
            await fn();
            await load(q);
            if (onChanged) onChanged();
        } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
        setBusy(null);
    };

    const headers = { "X-Practice-Token": token };
    const connect = (tenantId) => act(async () => {
        await axios.post(`${API}/connect/practice/requests`, { tenant_id: tenantId }, { headers });
        toast.success("Connection request sent to the lab");
    }, tenantId);
    const respond = (connId, action) => act(async () => {
        await axios.post(`${API}/connect/practice/requests/${connId}/respond`, { action }, { headers });
        toast.success(action === "accept" ? "Lab connected — you can now send it scan orders" : "Invitation declined");
    }, connId);
    const disconnect = (connId) => act(async () => {
        await axios.delete(`${API}/connect/practice/connections/${connId}`, { headers });
        toast.success("Disconnected");
    }, connId);

    if (loading) return <div className="py-24 text-center"><Loader2 className="animate-spin mx-auto text-slate-400" /></div>;

    return (
        <div className="space-y-8" data-testid="find-lab-page">
            {conns.incoming.length > 0 && (
                <section data-testid="fl-incoming">
                    <h3 className="text-[13px] font-semibold uppercase tracking-wide text-amber-600 mb-3 flex items-center gap-1.5"><Inbox size={14} />Lab invitations ({conns.incoming.length})</h3>
                    <div className="space-y-2">
                        {conns.incoming.map((c) => (
                            <div key={c.conn_id} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-amber-200 bg-amber-50/60 px-5 py-4" data-testid={`fl-incoming-${c.conn_id}`}>
                                <div>
                                    <div className="text-[14.5px] font-medium text-slate-900">{c.lab_name}</div>
                                    {c.message && <div className="text-[12.5px] text-slate-600 mt-0.5 italic">&ldquo;{c.message}&rdquo;</div>}
                                </div>
                                <div className="flex gap-2">
                                    <button onClick={() => respond(c.conn_id, "accept")} disabled={busy === c.conn_id} data-testid={`fl-accept-${c.conn_id}`}
                                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-400 text-white text-[13px] font-medium">
                                        <Check size={13} />Accept
                                    </button>
                                    <button onClick={() => respond(c.conn_id, "decline")} disabled={busy === c.conn_id} data-testid={`fl-decline-${c.conn_id}`}
                                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl border border-slate-300 text-slate-700 hover:border-rose-400 hover:text-rose-600 text-[13px] font-medium">
                                        <X size={13} />Decline
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            <section data-testid="fl-connected">
                <h3 className="text-[13px] font-semibold uppercase tracking-wide text-slate-500 mb-3 flex items-center gap-1.5"><Link2 size={14} />My connected labs ({conns.connected.length})</h3>
                {conns.connected.length === 0
                    ? <div className="rounded-2xl border border-dashed border-slate-300 py-10 text-center text-slate-400 text-[13.5px]" data-testid="fl-connected-empty">You&apos;re not connected to any lab yet — find one below and send a request.</div>
                    : (
                        <div className="space-y-2">
                            {conns.connected.map((c) => (
                                <div key={c.conn_id} className="rounded-2xl border border-slate-200 overflow-hidden" data-testid={`fl-connected-${c.conn_id}`}>
                                    <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4">
                                        <div className="text-[14.5px] font-medium text-slate-900">{c.lab_name}</div>
                                        <div className="flex items-center gap-2">
                                            <span className="px-2.5 py-0.5 rounded-full text-[11.5px] font-medium bg-emerald-100 text-emerald-800">Connected</span>
                                            <button onClick={() => setChatOpen(chatOpen === c.conn_id ? null : c.conn_id)} data-testid={`fl-chat-${c.conn_id}`}
                                                className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-[12.5px] font-medium transition-colors ${chatOpen === c.conn_id ? "bg-slate-900 text-white" : "border border-slate-300 text-slate-600 hover:border-cyan-500 hover:text-cyan-700"}`}>
                                                <MessageSquare size={12} />Chat with lab
                                                {c.unread > 0 && <span data-testid={`fl-chat-unread-${c.conn_id}`} className="min-w-[16px] h-[16px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center leading-none">{c.unread}</span>}
                                            </button>
                                            <button onClick={() => disconnect(c.conn_id)} disabled={busy === c.conn_id} data-testid={`fl-disconnect-${c.conn_id}`}
                                                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl border border-slate-300 text-slate-600 hover:border-rose-400 hover:text-rose-600 text-[12.5px]">
                                                <Unplug size={12} />Disconnect
                                            </button>
                                        </div>
                                    </div>
                                    {chatOpen === c.conn_id && (
                                        <div className="border-t border-slate-100 px-5 py-4 bg-slate-50/50">
                                            <ConnectionChat
                                                base={`${API}/connect/practice/connections/${c.conn_id}/messages`}
                                                headers={headers}
                                                mySide="practice"
                                                onRead={() => {
                                                    setConns((d) => ({ ...d, connected: d.connected.map((x) => (x.conn_id === c.conn_id ? { ...x, unread: 0 } : x)) }));
                                                    if (onChanged) onChanged();
                                                }}
                                            />
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
            </section>

            <section data-testid="fl-directory">
                <h3 className="text-[13px] font-semibold uppercase tracking-wide text-slate-500 mb-3 flex items-center gap-1.5"><FlaskConical size={14} />Find a lab</h3>
                <div className="relative mb-4 max-w-md">
                    <Search size={15} className="absolute start-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="fl-search"
                        placeholder="Search labs by name…"
                        className="w-full ps-10 pe-4 py-2.5 rounded-xl border border-slate-300 bg-white outline-none focus:border-cyan-500 text-[13.5px]" />
                </div>
                {labs.length === 0
                    ? <div className="py-10 text-center text-slate-400 text-[13.5px]" data-testid="fl-directory-empty">No labs match this search.</div>
                    : (
                        <div className="grid sm:grid-cols-2 gap-3">
                            {labs.map((l) => (
                                <div key={l.tenant_id} className="rounded-2xl border border-slate-200 p-5 flex flex-col gap-3" data-testid={`fl-lab-${l.slug}`}>
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="flex items-center gap-3">
                                            {l.photo_url
                                                ? <img src={`${process.env.REACT_APP_BACKEND_URL}${l.photo_url}`} alt={l.name} className="w-12 h-12 rounded-xl object-cover border border-slate-200" />
                                                : l.logo_url
                                                    ? <img src={l.logo_url} alt={l.name} className="w-12 h-12 rounded-xl object-contain bg-white border border-slate-200 p-1" />
                                                    : <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center text-slate-400"><FlaskConical size={18} /></div>}
                                            <div>
                                                <div className="text-[14.5px] font-medium text-slate-900">{l.name}</div>
                                                {(l.city || l.country) && (
                                                    <div className="text-[12px] text-slate-500 flex items-center gap-1"><MapPin size={11} />{[l.city, l.country].filter(Boolean).join(", ")}</div>
                                                )}
                                            </div>
                                        </div>
                                        {l.connection_status === "accepted" && <span className="px-2.5 py-0.5 rounded-full text-[11.5px] font-medium bg-emerald-100 text-emerald-800">Connected</span>}
                                        {l.connection_status === "pending" && l.requested_by === "practice" && <span className="px-2.5 py-0.5 rounded-full text-[11.5px] font-medium bg-amber-100 text-amber-800">Request sent</span>}
                                        {l.connection_status === "pending" && l.requested_by === "lab" && <span className="px-2.5 py-0.5 rounded-full text-[11.5px] font-medium bg-cyan-100 text-cyan-800">Invited you</span>}
                                        {!["accepted", "pending"].includes(l.connection_status) && (
                                            <button onClick={() => connect(l.tenant_id)} disabled={busy === l.tenant_id} data-testid={`fl-connect-${l.tenant_id}`}
                                                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-slate-900 hover:bg-slate-700 disabled:bg-slate-400 text-white text-[12.5px] font-medium">
                                                <Link2 size={12} />Connect
                                            </button>
                                        )}
                                    </div>
                                    {l.description && <p className="text-[12.5px] text-slate-600 leading-snug">{l.description}</p>}
                                    {(l.turnaround || (l.services || []).length > 0) && (
                                        <div className="flex flex-wrap items-center gap-1.5">
                                            {l.turnaround && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-cyan-50 text-cyan-800 text-[11px] font-medium"><Clock size={10} />{l.turnaround}</span>}
                                            {(l.services || []).slice(0, 4).map((s) => <span key={s} className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[11px]">{s}</span>)}
                                            {(l.services || []).length > 4 && <span className="text-[11px] text-slate-400">+{l.services.length - 4} more</span>}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
            </section>
        </div>
    );
}
